import * as React from 'react'
import moment from 'moment'
import { VehicleLockModel } from '@/app/models/vehicle/vehicleLockModel'
import useVehicleService from '../../../services/VehicleService'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { getVehicleLocksColumns } from '@/modules/vehicles/constants/tables'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface SingleProps {
  companyId: string
  vehicleId: string
}

const Single: React.FC<SingleProps> = ({ companyId, vehicleId }) => {
  const { getVehicleLocks } = useVehicleService()
  const { t } = useLocalize()
  const [locks, setLocks] = React.useState<VehicleLockModel[]>([])
  const [loading, setLoading] = React.useState(false)

  const VehicleLocksColumns = React.useMemo(() => getVehicleLocksColumns(t), [t])

  React.useEffect(() => {
    const fetchLocks = async () => {
      setLoading(true)
      try {
        const data = await getVehicleLocks({ companyId, vehicleId })
        setLocks(data || [])
      } finally {
        setLoading(false)
      }
    }
    fetchLocks()
  }, [companyId, vehicleId])

  const formatDate = (value: any) => {
    if (!value) return '—'
    const m = moment(value)
    return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '—'
  }

  if (loading) {
    return (
      <div className="tw-grid tw-h-full tw-place-items-center">
        <LoaderIcon className="tw-w-16 tw-animate-spin" />
      </div>
    )
  }

  return (
    <section className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-relative tw-flex-1 tw-overflow-x-auto">
        {locks.length > 0 ? (
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              toggleAll={() => {}}
              isSelectedAny={false}
              columns={VehicleLocksColumns}
              sortConfig={null}
              onHeaderClick={undefined}
              disabledToggle
            />
            <tbody>
              {locks.map((lock, index) => (
                <tr
                  key={index}
                  className="notches-border--right tw-w-fit tw-border-b tw-bg-white"
                >
                  <td className="tw-py-3.5 tw-pl-2.5 tw-pr-3" />
                  <TableTextRow bolder={false}>{formatDate(lock.lockBeginTime)}</TableTextRow>
                  <TableTextRow bolder={false}>{formatDate(lock.lockEndTime)}</TableTextRow>
                  <TableTextRow bolder={false}>{lock.lockingCompany || '—'}</TableTextRow>
                  <TableTextRow bolder={false}>{lock.lockingCardNumber || '—'}</TableTextRow>
                  <TableTextRow bolder={false}>{lock.vehicleIdentificationId ?? '—'}</TableTextRow>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <TableHeader
              opacity
              toggleAll={() => {}}
              isSelectedAny={false}
              columns={VehicleLocksColumns}
              sortConfig={null}
              disabledToggle
            />
            <TableEmpty title={t('vehicleModule.no-locks')} />
          </>
        )}
      </div>
    </section>
  )
}

export default Single
